import { Loader2, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Common/button';
import notify from '@/lib/notify';
import {
  InterviewTemplate,
  Template,
  Question as BackendQuestion,
  TemplateQuestionInForm,
} from '@/types';
import {
  deleteInterviewTemplate,
  fetchInterviewTemplates,
} from '@/services/interviewTemplateService';
import { v4 as uuidv4 } from 'uuid';
import InterviewTemplateFormModal from './InterviewTemplateFormModal';

/**
 * Interview (guided) template management rendered as a table with a modal editor.
 * Lives in the organization "Templates" tab alongside document templates.
 */
export const InterviewTemplatesSection: React.FC = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const mapBackendToFrontendTemplate = useCallback(
    (backendTpl: InterviewTemplate & { can_delete?: boolean }): Template => ({
      id: backendTpl.id,
      name: backendTpl.name,
      description: backendTpl.description || undefined,
      questions: backendTpl.questions.map(
        (q: BackendQuestion): TemplateQuestionInForm => ({
          id: uuidv4(),
          text: q.text,
          order: q.order,
        }),
      ),
      can_delete: backendTpl.can_delete ?? true,
    }),
    [],
  );

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const interviewTemplates = await fetchInterviewTemplates();
      setTemplates(interviewTemplates.map(mapBackendToFrontendTemplate));
    } catch (err: any) {
      console.error('Erreur lors du chargement des templates:', err);
      setError(err.response?.data?.detail || err.message || t('templatesSection.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [mapBackendToFrontendTemplate, t]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (id: string) => {
    setEditingId(id);
    setFormOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!globalThis.confirm(t('templatesSection.deleteConfirm', { name }))) return;
    setIsLoading(true);
    try {
      await deleteInterviewTemplate(id);
      await loadTemplates();
    } catch (err: any) {
      console.error('Erreur lors de la suppression du template:', err);
      notify.error(
        err.response?.data?.detail || err.message || t('templatesSection.deleteError'),
      );
      setIsLoading(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground">
            {t('organizationTemplates.interviewsTitle', "Modèles d'entretiens")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              'organizationTemplates.interviewsDescription',
              "Modèles d'entretiens guidés utilisés dans l'organisation.",
            )}
          </p>
        </div>
        <Button onClick={openCreate} size="sm" className="shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          {t('organizationTemplates.newInterview', 'Nouveau modèle')}
        </Button>
      </div>

      {error && (
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded"
          role="alert"
        >
          <div className="flex items-center mb-1">
            <TriangleAlert className="h-5 w-5 mr-2" />
            <p className="font-bold text-sm">{t('templatesSection.error')}</p>
          </div>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('templatesSection.loading')}
        </p>
      ) : templates.length === 0 && !error ? (
        <p className="text-sm text-muted-foreground py-4">
          {t('organizationTemplates.emptyInterviews', "Aucun modèle d'entretien pour le moment.")}
        </p>
      ) : templates.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm text-left text-muted-foreground">
            <thead className="text-xs uppercase bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t('organizationTemplates.columns.name', 'Nom')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('organizationTemplates.columns.description', 'Description')}
                </th>
                <th scope="col" className="px-4 py-3 text-center">
                  {t('organizationTemplates.columns.questions', 'Questions')}
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  {t('organizationTemplates.columns.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium text-foreground">{template.name}</td>
                  <td className="px-4 py-3">
                    <span className="line-clamp-1">{template.description || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-center">{template.questions?.length ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-end gap-1">
                      {template.can_delete === false && (
                        <p className="text-xs text-red-600 dark:text-red-400">
                          {t(
                            'layout.pages.templates.cannotDeleteBecauseUsed',
                            'Cannot be deleted because it is used',
                          )}
                        </p>
                      )}
                      <div className="flex justify-end items-center gap-2">
                        <Button onClick={() => openEdit(template.id)} variant="outline" size="sm">
                          <Pencil className="h-3.5 w-3.5 mr-1.5" />
                          {t('templatesSection.modify')}
                        </Button>
                        <Button
                          onClick={() => handleDelete(template.id, template.name)}
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={isLoading || template.can_delete === false}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          {t('templatesSection.delete')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <InterviewTemplateFormModal
        open={formOpen}
        templateId={editingId}
        onClose={() => setFormOpen(false)}
        onSaved={loadTemplates}
      />
    </section>
  );
};

export default InterviewTemplatesSection;
